import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog, Button, Field, TextInput, Select, Banner, Spinner, SimpleTable, Tag,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../services/api.js';
import { FormGrid } from '../next/ui';

/**
 * GradeFormDialogNext — the grade dialog in the new design. Same logic and server calls as GradeFormDialog:
 * per-registration bulk entry (member / exam), single grade edit, manual entry by personal code / exam code.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

const GradeFormDialogNext = ({ open, onClose, exam = null, member = null, grade = null, onSaved }) => {
  const isEdit = !!grade?._id;

  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [results, setResults]         = useState(null);

  const [editScore, setEditScore]     = useState('');

  const [manualMode, setManualMode]   = useState(false);
  const [personalCode, setPersonalCode] = useState('');
  const [examCode, setExamCode]       = useState('');
  const [manualScore, setManualScore] = useState('');
  const [manualTractate, setManualTractate] = useState('');
  const [manualPeriodId, setManualPeriodId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [resolvedMember, setResolvedMember] = useState(null);
  const [resolvedExam, setResolvedExam]     = useState(null);
  const [lookupLoading, setLookupLoading]   = useState(false);

  /** בונה רשימת שורות לפי הרשמות + ציונים קיימים */
  const buildRowsFromRegistrations = (registrations, grades, lockedSide) => {
    const newRows = [];
    const gradeMap = new Map();
    for (const g of grades) {
      const mId = g.memberId?._id || g.memberId;
      const eId = g.examId?._id || g.examId;
      const pId = g.examPeriodId?._id || g.examPeriodId;
      gradeMap.set(`${mId}|${eId || pId || 'gemara'}|${pId || ''}|${g.tractate || ''}`, g);
    }

    for (const reg of registrations) {
      if (reg.status !== 'registered') continue;
      const mId   = reg.memberId?._id || reg.memberId;
      const mName = reg.memberId
        ? `${reg.memberId.firstName || ''} ${reg.memberId.lastName || ''}`.trim()
        : '';
      const eId   = reg.examId?._id || reg.examId;
      const pId   = reg.periodId?._id || reg.periodId;
      const eName = reg.examId?.name || '';
      const eCode = reg.examId?.code || '';

      if (reg.gemaraSelection && reg.gemaraSelection.length > 0) {
        for (const sel of reg.gemaraSelection) {
          const key = `${mId}|${eId || pId || 'gemara'}|${pId || ''}|${sel.tractate}`;
          const existing = gradeMap.get(key);
          newRows.push({
            key,
            memberId: mId,
            memberName: mName,
            examId: eId || null,
            examPeriodId: pId || null,
            examName: eName || 'גמרא',
            examCode: eCode,
            tractate: sel.tractate,
            ranges: sel.ranges,
            existing,
            score: existing?.score ?? '',
          });
        }
      } else if (eId) {
        const key = `${mId}|${eId}|${pId || ''}|`;
        const existing = gradeMap.get(key);
        newRows.push({
          key,
          memberId: mId,
          memberName: mName,
          examId: eId,
          examPeriodId: pId || null,
          examName: eName,
          examCode: eCode,
          tractate: null,
          existing,
          score: existing?.score ?? '',
        });
      }
    }

    if (lockedSide === 'member') {
      newRows.sort((a, b) => (a.examName || '').localeCompare(b.examName || '', 'he'));
    } else {
      newRows.sort((a, b) => (a.memberName || '').localeCompare(b.memberName || '', 'he'));
    }
    return newRows;
  };

  useEffect(() => {
    if (!open) return;
    setError(''); setResults(null); setSaving(false);
    setLookupLoading(false); setManualMode(false);
    setPersonalCode(''); setExamCode('');
    setManualScore(''); setManualTractate(''); setManualPeriodId('');
    setResolvedMember(null); setResolvedExam(null);

    api.get('/exam-periods')
      .then(({ data }) => {
        const list = data.data || [];
        setPeriods(list);
        const active = list.find((p) => p.isActive);
        if (active) setManualPeriodId(active._id);
      })
      .catch(() => {});

    if (isEdit) {
      setEditScore(grade.score ?? '');
      setRows([]);
      return;
    }

    if (!member?._id && !exam?._id) {
      setManualMode(true);
      setRows([]);
      return;
    }

    const lockedSide = member?._id ? 'member' : 'exam';
    const params = lockedSide === 'member'
      ? { memberId: member._id, all: 1, status: 'registered' }
      : { examId: exam._id, all: 1, status: 'registered' };
    const gradeParams = lockedSide === 'member'
      ? { memberId: member._id, all: 1 }
      : { examId: exam._id, all: 1 };

    setLoading(true);
    Promise.all([
      api.get('/registrations', { params }),
      api.get('/grades', { params: gradeParams }),
    ])
      .then(([regRes, gradesRes]) => {
        const regs = regRes.data?.data || [];
        const gs   = gradesRes.data?.data || [];
        const built = buildRowsFromRegistrations(regs, gs, lockedSide);
        setRows(built);
        if (built.length === 0) setManualMode(true);
      })
      .catch((e) => setError(e.response?.data?.error || 'שגיאה בטעינת הרשמות'))
      .finally(() => setLoading(false));
  }, [open, isEdit, grade, member, exam]);

  const updateRowScore = (key, val) => {
    setRows((prev) => prev.map((r) => r.key === key ? { ...r, score: val } : r));
  };

  const stats = useMemo(() => {
    let toCreate = 0, toUpdate = 0, untouched = 0;
    for (const r of rows) {
      const s = r.score === '' || r.score === null ? null : parseFloat(r.score);
      if (s === null) { untouched++; continue; }
      if (r.existing) {
        if (r.existing.score !== s) toUpdate++;
        else untouched++;
      } else {
        toCreate++;
      }
    }
    return { toCreate, toUpdate, untouched };
  }, [rows]);

  const handleSaveBulk = async () => {
    setError('');
    const errs = [];
    let created = 0, updated = 0;
    setSaving(true);
    for (const r of rows) {
      const raw = r.score;
      if (raw === '' || raw === null || raw === undefined) continue;
      const s = parseFloat(raw);
      if (isNaN(s) || s < 0) {
        errs.push(`${r.examName}${r.tractate ? ` / ${r.tractate}` : ''}${r.memberName ? ` — ${r.memberName}` : ''}: ציון לא תקין (חייב להיות 0 ומעלה)`);
        continue;
      }
      try {
        if (r.existing) {
          if (r.existing.score === s) continue;
          await api.put(`/grades/${r.existing._id}`, {
            score: s,
            tractate: r.tractate || null,
            version: r.existing.version,
          });
          updated++;
        } else {
          const body = r.examId
            ? { memberId: r.memberId, examId: r.examId, examPeriodId: r.examPeriodId || null, score: s, tractate: r.tractate || null }
            : { memberId: r.memberId, examPeriodId: r.examPeriodId, score: s, tractate: r.tractate || null };
          await api.post('/grades', body);
          created++;
        }
      } catch (e) {
        errs.push(`${r.examName}${r.tractate ? ` / ${r.tractate}` : ''}${r.memberName ? ` — ${r.memberName}` : ''}: ${e.response?.data?.error || 'שגיאה'}`);
      }
    }
    setSaving(false);
    setResults({ created, updated, errors: errs });
    onSaved?.();
    if (errs.length === 0) setTimeout(() => onClose?.(), 1200);
  };

  const handleSaveEdit = async () => {
    setError('');
    const s = parseFloat(editScore);
    if (isNaN(s) || s < 0) return setError('ציון לא תקין (חייב להיות מספר חיובי)');
    setSaving(true);
    try {
      await api.put(`/grades/${grade._id}`, {
        score: s,
        tractate: grade.tractate || null,
        version: grade.version,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally { setSaving(false); }
  };

  const lookupMember = async () => {
    if (!personalCode) return;
    setLookupLoading(true); setError('');
    try {
      const { data } = await api.get('/members', { params: { personalCode, pageSize: 1 } });
      if (data?.data?.length) setResolvedMember(data.data[0]);
      else { setResolvedMember(null); setError(`לא נמצא חבר עם קוד אישי ${personalCode}`); }
    } catch (e) { setError(e.response?.data?.error || 'שגיאת חיפוש'); }
    finally { setLookupLoading(false); }
  };
  const lookupExam = async () => {
    if (!examCode) return;
    setLookupLoading(true); setError('');
    try {
      const { data } = await api.get('/exams', { params: { code: examCode, pageSize: 1 } });
      if (data?.data?.length) setResolvedExam(data.data[0]);
      else { setResolvedExam(null); setError(`לא נמצא מבחן עם קוד ${examCode}`); }
    } catch (e) { setError(e.response?.data?.error || 'שגיאת חיפוש'); }
    finally { setLookupLoading(false); }
  };

  // האם להציג שדה מסכת במצב ידני — רק כשמדובר במבחן גמרא
  const effectiveExam  = exam?._id ? exam : resolvedExam;
  const isGemaraExam   = IS_OPEN_GEMARA ||
    effectiveExam?.type === 'time' && (effectiveExam?.subject || '').includes('גמרא');

  const handleSaveManual = async () => {
    setError('');
    const memberObj = member?._id ? member : resolvedMember;
    const examObj   = exam?._id   ? exam   : resolvedExam;
    const s = parseFloat(manualScore);
    if (!memberObj?._id) return setError('יש לבחור חבר תקין');
    if (!IS_OPEN_GEMARA && !examObj?._id) return setError('יש לבחור מבחן תקין');
    if (!manualPeriodId) return setError('יש לבחור מועד');
    if (isNaN(s) || s < 0) return setError('ציון לא תקין (חייב להיות מספר חיובי)');

    setSaving(true);
    try {
      const body = IS_OPEN_GEMARA
        ? {
            memberId: memberObj._id,
            examPeriodId: manualPeriodId,
            score: s,
            tractate: manualTractate || null,
          }
        : {
            memberId: memberObj._id,
            examId: examObj._id,
            examPeriodId: manualPeriodId,
            score: s,
            tractate: null,
          };
      await api.post('/grades', body);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally { setSaving(false); }
  };

  // האם יש בכלל שורות עם מסכת (רלוונטי למבחני גמרא בלבד)
  const hasTractateRows = rows.some((r) => r.tractate);

  const lockedSide = member?._id ? 'member' : exam?._id ? 'exam' : null;
  const headerLabel = isEdit
    ? 'עריכת ציון'
    : member?._id
      ? `הזנת ציונים — ${member.firstName || ''} ${member.lastName || ''}`.trim()
      : exam?._id
        ? `הזנת ציונים — ${exam.name || ''}${exam.code ? ` (קוד ${exam.code})` : ''}`
        : 'הזנת ציון';

  const footer = (
    <>
      <Button variant="ghost" disabled={saving} onClick={onClose}>סגירה</Button>
      {isEdit ? <Button variant="primary" disabled={saving} onClick={handleSaveEdit}>{saving ? 'שומר…' : 'שמירה'}</Button> : null}
      {!isEdit && manualMode ? <Button variant="primary" disabled={saving} onClick={handleSaveManual}>{saving ? 'שומר…' : 'שמור ציון'}</Button> : null}
      {!isEdit && !manualMode && rows.length > 0 ? (
        <Button variant="primary" disabled={saving || (stats.toCreate + stats.toUpdate === 0)} onClick={handleSaveBulk}>
          {saving ? 'שומר…' : `שמור ${stats.toCreate + stats.toUpdate} ציונים`}
        </Button>
      ) : null}
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} title={headerLabel} icon="check" size="lg" footer={footer}>
      <div className="nx-stack">
        {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
        {results ? (
          <Banner tone={results.errors.length ? 'warning' : 'success'}>
            {`נוצרו ${results.created}, עודכנו ${results.updated}`}
            {results.errors.length ? <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>{results.errors.map((er, i) => <li key={i}>{er}</li>)}</ul> : null}
          </Banner>
        ) : null}

        {isEdit ? (
          <>
            <p className="nx-desc">{grade?.examId?.name || ''}{grade?.tractate ? ` — ${grade.tractate}` : ''}</p>
            <Field label="ציון"><TextInput type="number" ltr min={0} step={1} value={editScore} onChange={(e) => setEditScore(e.target.value)} data-autofocus onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); }} /></Field>
          </>
        ) : null}

        {!isEdit && loading ? <div className="ck-empty"><Spinner size={26} /></div> : null}

        {!isEdit && !loading && !manualMode && rows.length > 0 ? (
          <>
            <p className="nx-desc">הזינו ציון לכל מבחן/מסכת. שורות ריקות לא יישמרו. שורות עם ציון זהה לקיים יידלגו.</p>
            <SimpleTable
              rowKey="key"
              rows={rows}
              maxHeight={420}
              columns={[
                {
                  key: 'who',
                  label: lockedSide === 'exam' ? 'חבר' : 'מבחן',
                  render: (r) => (
                    <span>
                      <b>{lockedSide === 'exam' ? r.memberName : r.examName}</b>
                      {lockedSide === 'member' && r.examCode ? <span className="ck-muted">{` (${r.examCode})`}</span> : null}
                    </span>
                  ),
                },
                ...(hasTractateRows ? [{
                  key: 'tractate',
                  label: 'מסכת / דפים',
                  render: (r) => (r.tractate ? (
                    <span>{r.tractate}{r.ranges?.length ? <span className="ck-muted">{` (${r.ranges.map((rg) => `${rg.from}–${rg.to}`).join(', ')})`}</span> : null}</span>
                  ) : '—'),
                }] : []),
                { key: 'existing', label: 'ציון קיים', render: (r) => (r.existing ? <Tag tone={r.existing.score >= 90 ? 'success' : 'neutral'}>{r.existing.score}</Tag> : '—') },
                {
                  key: 'score',
                  label: 'ציון חדש',
                  width: 130,
                  render: (r) => <TextInput type="number" ltr min={0} step={1} style={{ width: 110 }} value={r.score} onChange={(e) => updateRowScore(r.key, e.target.value)} />,
                },
              ]}
            />
            <div className="nx-row" style={{ gap: 12, alignItems: 'center' }}>
              <small className="ck-muted">{`לשמירה: ${stats.toCreate} חדשים, ${stats.toUpdate} לעדכון, ${stats.untouched} ללא שינוי`}</small>
              <Button size="sm" variant="ghost" icon="edit" onClick={() => setManualMode(true)}>הזנה ידנית</Button>
            </div>
          </>
        ) : null}

        {!isEdit && !loading && manualMode ? (
          <>
            {rows.length === 0 && lockedSide ? (
              <Banner tone="info" icon="info">{`לא נמצאו הרשמות פעילות${lockedSide === 'member' ? ' לחבר זה' : ' למבחן זה'}. ניתן להוסיף ציון ידנית.`}</Banner>
            ) : null}
            <FormGrid cols={2}>
              <Field label="חבר">
                {member?._id ? (
                  <TextInput readOnly value={`${`${member.firstName || ''} ${member.lastName || ''}`.trim()}${member.personalCode ? ` (קוד אישי: ${member.personalCode})` : ''}`} />
                ) : (
                  <div className="ck-input-adorn">
                    <TextInput type="number" ltr placeholder="קוד אישי" value={personalCode} onChange={(e) => setPersonalCode(e.target.value.replace(/\D/g, ''))} onBlur={lookupMember} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupMember(); } }} data-autofocus />
                    {lookupLoading ? <Spinner size={16} /> : resolvedMember ? <Tag tone="success" icon="check">{`${resolvedMember.firstName} ${resolvedMember.lastName}`}</Tag> : null}
                  </div>
                )}
              </Field>
              <Field label="מבחן">
                {exam?._id ? (
                  <TextInput readOnly value={`${exam.name || ''}${exam.code ? ` (קוד: ${exam.code})` : ''}`} />
                ) : (
                  <div className="ck-input-adorn">
                    <TextInput ltr placeholder="קוד מבחן" value={examCode} onChange={(e) => setExamCode(e.target.value)} onBlur={lookupExam} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupExam(); } }} />
                    {lookupLoading ? <Spinner size={16} /> : resolvedExam ? <Tag tone="success" icon="check">{resolvedExam.name}</Tag> : null}
                  </div>
                )}
              </Field>
              <Field label="מועד *"><Select value={manualPeriodId} onChange={setManualPeriodId} placeholder="בחרו מועד" options={periods.map((p) => ({ value: p._id, label: p.name }))} /></Field>
              <Field label="ציון *"><TextInput type="number" ltr min={0} step={1} value={manualScore} onChange={(e) => setManualScore(e.target.value)} /></Field>
              {isGemaraExam ? <Field label="מסכת (אופציונלי)"><TextInput value={manualTractate} onChange={(e) => setManualTractate(e.target.value)} /></Field> : null}
            </FormGrid>
            {rows.length > 0 ? <div><Button size="sm" variant="ghost" icon="right" onClick={() => setManualMode(false)}>חזרה לרשימת הרשמות</Button></div> : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
};

export default GradeFormDialogNext;
